import React, { useEffect, useMemo, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import Content from 'studio/components/Content/Content';
import { useLoader } from 'components/loaders/FadeLoader';
import ManageSearchIcon from '@mui/icons-material/ManageSearchRounded';
import { useMessage } from 'studio/components/Message';
import { trackEvent } from 'utils/eventTracking';
import services from 'services';
import PageTitle from 'studio/components/PageTitle';
import { MonacoDiffEditor, MonacoEditor } from 'components/monaco';
import ReactTableUI from 'components/chart/ReactTableUI';
import styles from './index.module.scss';
import Pagination from 'components/chart/table-plot/Pagination';
import { vqlLanguageId, vqlMonarchLanguageDefinition, vqlTheme } from 'forms/VqlSyntaxHighlighter';
import Tabs from 'studio/components/Tabs/Tabs';
import Tab from '@mui/material/Tab';

const QueryEditor = ({ knowledgeGraph }) => {
  useEffect(() => {
    trackEvent('Query Editor Opened');
  }, []);

  const { errorMessage } = useMessage();
  const { loading, withLoader } = useLoader();
  const [result, setResult] = useState(null); // result of the executed query

  /* Stores whether the SQL (expressed with 'sql') or VQL (expressed with 'vql') editor is active. */
  const [activeMode, setActiveMode] = useState('sql');

  /**
   * Stores which output should be shown.
   *
   * it is one of the following: 'results', 'vql', 'sql', 'intermediateVqls'
   * 'results' shows the database results for the (SQL or VQL) query
   * 'vql' shows the fully rewritten VQL query generated from the VQL query (visible only if VQL mode is active)
   * 'sql' shows the SQL query generated from the VQL query (visible only if VQL mode is active)
   * 'intermediateVqls' shows the intermediate VQLs resulting from the VQL rewrite rules (visible only if VQL mode is active)
   */
  const [activeOutputView, setActiveOutputView] = useState('results');

  useEffect(() => {
    setActiveOutputView(activeOutputView => {
      if (!result) {
        return activeOutputView; // keep the current view if there is no result yet
      }
      if (!result.intermediateVqls) {
        return 'results'; // reset to results view when result comes from SQL (no intermediate VQLs)
      }
      if (activeOutputView === 'results' && !result.data) {
        // this can only happen for VQL queries, switch to intermediate VQLs view
        return 'intermediateVqls';
      }
      if (activeOutputView === 'vql' && !result.vql) {
        // this can only happen for VQL queries, switch to intermediate VQLs view
        return 'intermediateVqls';
      }
      if (activeOutputView === 'sql' && !result.sql) {
        // this can only happen for VQL queries, switch to intermediate VQLs view
        return 'intermediateVqls';
      }
      return activeOutputView; // keep the current view if it is still valid
    });
  }, [result]);

  const [selectedVqlIndex, setSelectedVqlIndex] = useState(null); // selected intermediate VQL
  const [lastExecutedQuery, setLastExecutedQuery] = useState(null); // last executed query (used for diff with first intermediate Vql)

  const { columns, data } = result?.data || {};

  /**
   * Editor in which the user writes the SQL or VQL query.
   */
  const queryEditorRef = useRef(null);

  /**
   * Use ref to avoid a stale reference in the editor action.
   *
   * Editor action is set up only once in the beginning and it needs to execute the closure we are defining here.
   * This closure is updated on every re-rendering.
   * If we did not use a reference, then the editor action would always invoke the first closure that was set up here,
   * which uses old information (in the meantime, various state components that this closure accesses may have changed).
   */
  const executeQuery = useRef();
  executeQuery.current = async () => {
    if (!queryEditorRef.current) {
      errorMessage('Editor is not yet initialized. Please wait a moment and try again.');
      return;
    }
    const query = queryEditorRef.current.getModel().getValue();

    const service = activeMode === 'sql' ? services.querySql : services.queryVql;

    if (activeMode === 'sql') {
      trackEvent('SQL query executed in Query Editor');
    } else {
      trackEvent('VQL query executed in Query Editor');
    }

    const response = await withLoader(service(knowledgeGraph.data.id, query));
    if (response.success) {
      setSelectedVqlIndex(null); // Reset index to avoid out of bounds errors
      setLastExecutedQuery(query); // Store the query that was executed (only relevant if successful response)

      setResult(response.data);

      // this needs to come after setResult, since the new index makes sense only once the result has been set
      // if we add it before setResult, then setting the index may trigger a separate rendering cycle before result is set
      if (response.data?.intermediateVqls?.length > 0) {
        // set the index to the first intermediate VQL if such a VQL exists
        setSelectedVqlIndex(0);
      }

      if (response.data?.error) {
        errorMessage(response.data?.error);
      }
    } else {
      // Note: we currently keep the old result displayed, if there was one
      const message = response?.response?.data?.data?.message || 'An error occurred when executing the query.';
      errorMessage(message);
    }
  };

  const queryEditorDidMount = (newEditor, monaco) => {
    queryEditorRef.current = newEditor;

    // Query using Ctrl/Cmd + Enter
    newEditor.addAction({
      id: 'execute-query',
      label: 'Execute Query',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter],
      // Here it is important that we access the closure via a reference to ensure invoking the latest closure
      // Note: Cannot replace this by just executeQuery.current (otherwise would capture just the initial closure)
      run: () => executeQuery.current()
    });
  };

  const vqlOutputEditorDidMount = (newEditor, _) => {
    newEditor.updateOptions({
      readOnly: true,
      minimap: { enabled: false },
      scrollBeyondLastLine: false,
      lineNumbers: 'on',
      renderLineHighlight: 'all',
      scrollbar: {
        verticalScrollbarSize: 6,
        horizontalScrollbarSize: 6
      },
      border: 'none'
    });
  };

  const editorWillMount = monaco => {
    // Register VQL language
    monaco.languages.register({ id: vqlLanguageId });
    monaco.languages.setMonarchTokensProvider(vqlLanguageId, vqlMonarchLanguageDefinition);
    monaco.editor.defineTheme('vqlTheme', vqlTheme);
  };

  const reactTableColumns = useMemo(() => {
    return (columns || []).map((columnName, idx) => ({
      Header: () => <div className={styles.columnHeader}>{columnName}</div>,
      width: 150,
      // Column name may not be unique, so we use the index as the accessor
      accessor: String(idx),
      name: columnName
    }));
  }, [columns]);

  const reactTableData = useMemo(() => {
    return (data || []).map(row => {
      const rowWithColumnNames = {};
      columns.forEach((_, idx) => {
        // the index here must match the accessor in reactTableColumns
        rowWithColumnNames[String(idx)] = row[idx];
      });
      return rowWithColumnNames;
    });
  }, [data, columns]);

  const scrollbarSize = 6;

  const renderIntermediateVqlsView = () => {
    return (
      <div className={styles.queryContainer} style={{ overflow: 'hidden' }}>
        <div style={{ display: 'flex', height: '500px', overflow: 'hidden' }}>
          {/* Buttons to cycle through VQLs and rule name list associated with the intermediate VQLs */}
          <div
            style={{
              width: '25%',
              borderRight: '1px solid #ddd',
              overflowY: 'auto',
              padding: '10px',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative'
            }}
          >
            <div
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 2,
                borderBottom: '1px solid #eee'
              }}
            ></div>
            <div style={{ height: '100%', overflowY: 'auto' }}>
              {result.intermediateVqls?.map((step, index) => (
                <div
                  key={index}
                  data-rule-index={index}
                  style={{
                    padding: '8px 12px',
                    marginBottom: '8px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    backgroundColor: selectedVqlIndex === index ? '#e3f2fd' : 'transparent',
                    border: selectedVqlIndex === index ? '1px solid #90caf9' : '1px solid #eee',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                  onClick={() => setSelectedVqlIndex(index)}
                >
                  <div style={{ fontWeight: 'bold' }}>
                    {index + 1}. {step.ruleName}
                  </div>
                  {step.rewritingStage && (
                    <div
                      style={{
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '0.7em',
                        fontWeight: 'bold',
                        backgroundColor: step.rewritingStage === 'semantic' ? '#e3f2fd' : '#f3e5f5',
                        color: step.rewritingStage === 'semantic' ? '#1976d2' : '#7b1fa2'
                      }}
                    >
                      {step.rewritingStage}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Intermediate VQL diff to previous VQL and additionally the intermediate VQL without displaying the diff */}
          <div style={{ flex: 1, padding: '10px', overflow: 'hidden' }}>
            {selectedVqlIndex !== null && result.intermediateVqls && (
              <div>
                <h3
                  style={{
                    marginTop: 0,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <span>
                    Rule {selectedVqlIndex + 1}: {result.intermediateVqls[selectedVqlIndex].ruleName}
                  </span>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        const newIndex = selectedVqlIndex - 1;
                        setSelectedVqlIndex(newIndex);
                        document
                          .querySelector(`[data-rule-index="${newIndex}"]`)
                          ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                      }}
                      disabled={selectedVqlIndex <= 0}
                      style={{
                        textTransform: 'none',
                        fontSize: '0.8rem',
                        borderColor: selectedVqlIndex <= 0 ? '#bdbdbd' : '#7b1fa2',
                        color: selectedVqlIndex <= 0 ? '#bdbdbd' : '#7b1fa2'
                      }}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        const newIndex = selectedVqlIndex + 1;
                        setSelectedVqlIndex(newIndex);
                        document
                          .querySelector(`[data-rule-index="${newIndex}"]`)
                          ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                      }}
                      disabled={selectedVqlIndex >= result.intermediateVqls.length - 1}
                      style={{
                        textTransform: 'none',
                        fontSize: '0.8rem',
                        borderColor: selectedVqlIndex >= result.intermediateVqls.length - 1 ? '#bdbdbd' : '#7b1fa2',
                        color: selectedVqlIndex >= result.intermediateVqls.length - 1 ? '#bdbdbd' : '#7b1fa2'
                      }}
                    >
                      Next
                    </Button>
                  </div>
                </h3>

                <div>
                  <div
                    style={{
                      backgroundColor: '#f5f5f5',
                      padding: '10px',
                      borderRadius: '4px',
                      fontFamily: 'Menlo, Monaco, "Courier New", monospace',
                      fontSize: '12px',
                      whiteSpace: 'pre-wrap',
                      marginBottom: '15px'
                    }}
                  >
                    <MonacoDiffEditor
                      editorDidMount={vqlOutputEditorDidMount}
                      language={vqlLanguageId}
                      theme="vqlTheme"
                      options={{
                        readOnly: true,
                        minimap: { enabled: false },
                        scrollBeyondLastLine: true,
                        lineNumbers: 'on',
                        renderLineHighlight: 'all',
                        scrollbar: {
                          verticalScrollbarSize: scrollbarSize,
                          horizontalScrollbarSize: scrollbarSize
                        }
                      }}
                      original={
                        selectedVqlIndex > 0 ? result.intermediateVqls[selectedVqlIndex - 1].vql : lastExecutedQuery
                      }
                      value={result.intermediateVqls[selectedVqlIndex].vql}
                    />
                  </div>
                </div>
              </div>
            )}

            {selectedVqlIndex === null && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  paddingTop: '20px',
                  color: '#666'
                }}
              >
                {result?.intermediateVqls?.length === 0
                  ? 'No rewrite rules were applied to this VQL query.'
                  : 'Select a rule from the list to see the VQL changes'}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderOutputContent = () => {
    if (!result) return null;

    switch (activeOutputView) {
      case 'results':
        return (
          <div className={styles.tableContainer}>
            <ReactTableUI
              columns={reactTableColumns}
              data={reactTableData}
              defaultPageSize={20}
              disableResizing={true}
              PaginationComponent={Pagination}
            />
          </div>
        );
      case 'vql':
        return (
          <div className={styles.queryContainer}>
            <MonacoEditor
              height="400px"
              language={vqlLanguageId}
              theme="vqlTheme"
              value={result.vql || 'No VQL query available'}
              editorDidMount={vqlOutputEditorDidMount}
              editorWillMount={editorWillMount}
            />
          </div>
        );
      case 'sql':
        return (
          <div className={styles.queryContainer}>
            <MonacoEditor
              height="400px"
              language="sql"
              theme="vs"
              value={result.sql || 'No SQL query available'}
              editorDidMount={vqlOutputEditorDidMount}
            />
          </div>
        );
      case 'intermediateVqls':
        return renderIntermediateVqlsView();
      default:
        return null;
    }
  };

  // resets the active output view whenever the active mode is set to SQL
  useEffect(() => activeMode === 'sql' && setActiveOutputView('results'), [activeMode]);

  return (
    <Content>
      <PageTitle icon={ManageSearchIcon}>Query Editor</PageTitle>

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Tabs value={activeMode} onChange={(_, value) => setActiveMode(value)}>
          <Tab label="SQL" value="sql" />
          <Tab label="VQL" value="vql" />
        </Tabs>

        <div style={{ height: '25vh' }}>
          <MonacoEditor
            height="100%"
            language={activeMode === 'sql' ? 'sql' : vqlLanguageId}
            theme={activeMode === 'sql' ? 'vs' : 'vqlTheme'}
            options={{
              selectOnLineNumbers: true,
              scrollBeyondLastLine: false,
              scrollbar: {
                verticalScrollbarSize: scrollbarSize,
                horizontalScrollbarSize: scrollbarSize
              },
              lineNumbersMinChars: 3,
              minimap: {
                enabled: false
              }
            }}
            editorDidMount={queryEditorDidMount}
            editorWillMount={editorWillMount}
          />
        </div>

        <div style={{ marginTop: '20px' }}>
          <Button disabled={loading} variant="contained" color="primary" onClick={executeQuery.current}>
            Execute {activeMode.toUpperCase()} Query
          </Button>
        </div>

        {result && (
          <>
            <Tabs
              value={activeOutputView}
              onChange={(_, value) => setActiveOutputView(value)}
              style={{ marginTop: 20 }}
            >
              {activeMode === 'vql' && result.intermediateVqls && (
                <Tab label="VQL Rewrite Rules" value="intermediateVqls" />
              )}
              {activeMode === 'vql' && result.vql && <Tab label="Fully rewritten VQL Query" value="vql" />}
              {activeMode === 'vql' && result.sql && <Tab label="SQL Query" value="sql" />}
              {result.data && <Tab label="Results" value="results" />}
            </Tabs>
            {renderOutputContent()}
          </>
        )}
      </div>
    </Content>
  );
};

export default QueryEditor;
